class TimeTrackingService {
  constructor() {
    this.isTracking = false;
    this.currentPlanId = null;
    this.startTime = null;
    this.elapsedTime = 0;
    this.worker = null;
    this.heartbeatInterval = null;
    this.storageKey = 'timeTracking';
    
    // Bind methods
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    this.handleBeforeUnload = this.handleBeforeUnload.bind(this);
    this.handleWorkerMessage = this.handleWorkerMessage.bind(this);
    
    // Initialize
    this.init();
  }

  init() {
    // Load persisted state
    this.loadState();
    
    // Setup event listeners
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    window.addEventListener('beforeunload', this.handleBeforeUnload);
    
    // Create Web Worker for background timing
    this.createWorker();
    
    // Setup heartbeat for server sync
    this.setupHeartbeat();
  }

  createWorker() {
    // Create Web Worker inline to avoid separate file
    const workerScript = `
      let startTime = null;
      let isRunning = false;
      let intervalId = null;

      self.onmessage = function(e) {
        const { action, data } = e.data;
        
        switch(action) {
          case 'start':
            if (!isRunning) {
              startTime = data.startTime || Date.now();
              isRunning = true;
              
              // Send updates every second
              intervalId = setInterval(() => {
                if (isRunning) {
                  const elapsed = Date.now() - startTime;
                  self.postMessage({ type: 'tick', elapsed });
                }
              }, 1000);
            }
            break;
            
          case 'pause':
            isRunning = false;
            if (intervalId) {
              clearInterval(intervalId);
              intervalId = null;
            }
            break;
            
          case 'stop':
            isRunning = false;
            startTime = null;
            if (intervalId) {
              clearInterval(intervalId);
              intervalId = null;
            }
            break;
            
          case 'getElapsed':
            const elapsed = isRunning && startTime ? Date.now() - startTime : 0;
            self.postMessage({ type: 'elapsed', elapsed });
            break;
        }
      };
    `;

    const blob = new Blob([workerScript], { type: 'application/javascript' });
    this.worker = new Worker(URL.createObjectURL(blob));
    this.worker.onmessage = this.handleWorkerMessage;
  }

  handleWorkerMessage(e) {
    const { type, elapsed } = e.data;
    
    if (type === 'tick') {
      this.elapsedTime = elapsed;
      this.saveState();
      
      // Trigger UI update
      this.notifyListeners('tick', { elapsed });
    } else if (type === 'elapsed') {
      this.elapsedTime = elapsed;
    }
  }

  startTracking(planId) {
    if (this.isTracking && this.currentPlanId === planId) {
      return; // Already tracking this plan
    }

    // Stop current tracking if different plan
    if (this.isTracking && this.currentPlanId !== planId) {
      this.stopTracking();
    }

    this.currentPlanId = planId;
    this.isTracking = true;
    this.startTime = Date.now();
    this.elapsedTime = 0;

    // Start worker
    this.worker.postMessage({ 
      action: 'start', 
      data: { startTime: this.startTime } 
    });

    this.saveState();
    this.notifyListeners('started', { planId });
  }

  pauseTracking() {
    if (!this.isTracking) return;

    this.isTracking = false;
    this.worker.postMessage({ action: 'pause' });
    
    this.saveState();
    this.notifyListeners('paused', { planId: this.currentPlanId });
  }

  resumeTracking() {
    if (this.isTracking || !this.currentPlanId) return;

    this.isTracking = true;
    this.startTime = Date.now() - this.elapsedTime;
    
    this.worker.postMessage({ 
      action: 'start', 
      data: { startTime: this.startTime } 
    });

    this.saveState();
    this.notifyListeners('resumed', { planId: this.currentPlanId });
  }

  stopTracking() {
    if (!this.isTracking && !this.currentPlanId) return;

    const finalElapsed = this.elapsedTime;
    const planId = this.currentPlanId;

    this.isTracking = false;
    this.currentPlanId = null;
    this.startTime = null;
    this.elapsedTime = 0;

    this.worker.postMessage({ action: 'stop' });
    
    // Save final timesheet entry
    this.saveTimesheetEntry(planId, finalElapsed);
    
    this.clearState();
    this.notifyListeners('stopped', { planId, elapsed: finalElapsed });
  }

  handleVisibilityChange() {
    if (document.hidden) {
      // Tab became hidden - pause tracking
      if (this.isTracking) {
        this.pauseTracking();
        this.wasTrackingBeforeHidden = true;
      }
    } else {
      // Tab became visible - resume if was tracking
      if (this.wasTrackingBeforeHidden && this.currentPlanId) {
        this.resumeTracking();
        this.wasTrackingBeforeHidden = false;
      }
    }
  }

  handleBeforeUnload() {
    // Save current state before window closes
    if (this.isTracking) {
      this.saveTimesheetEntry(this.currentPlanId, this.elapsedTime);
    }
  }

  async saveTimesheetEntry(planId, elapsedMs) {
    if (!planId || elapsedMs < 1000) return; // Don't save if less than 1 second

    const now = new Date();
    const startTime = new Date(now.getTime() - elapsedMs);
    
    try {
      // Import the CreateTimesheetApi dynamically
      const { default: CreateTimesheetApi } = await import('../api/timesheets/CreateTimesheetApi');
      
      const timesheetData = {
        planId,
        startTime: startTime.toISOString(),
        endTime: now.toISOString(),
        workDate: now.toISOString().split('T')[0],
        description: `Auto-tracked time: ${this.formatDuration(elapsedMs)}`
      };
      
      console.log('🔍 Sending timesheet data:', timesheetData);
      
      const response = await CreateTimesheetApi(
        planId,
        startTime.toISOString(), // Full datetime in ISO format
        now.toISOString(), // Full datetime in ISO format
        now.toISOString().split('T')[0], // YYYY-MM-DD format
        `Auto-tracked time: ${this.formatDuration(elapsedMs)}`
      );

      if (response.success) {
        console.log('✅ Auto-timesheet saved successfully:', this.formatDuration(elapsedMs));
        // Emit event to refresh timesheet list
        this.notifyListeners('timesheetSaved', { planId, elapsed: elapsedMs });
      } else {
        console.error('❌ Failed to save timesheet:', response);
        console.error('❌ Response details:', JSON.stringify(response, null, 2));
        this.storeFailedEntry({ planId, elapsedMs, timestamp: now.toISOString() });
      }
    } catch (error) {
      console.error('❌ Error saving timesheet entry:', error);
      // Store in local storage as backup
      this.storeFailedEntry({ planId, elapsedMs, timestamp: now.toISOString() });
    }
  }

  storeFailedEntry(timesheetData) {
    const failedEntries = JSON.parse(localStorage.getItem('failedTimesheets') || '[]');
    failedEntries.push(timesheetData);
    localStorage.setItem('failedTimesheets', JSON.stringify(failedEntries));
  }

  setupHeartbeat() {
    // Sync with server every 30 seconds
    this.heartbeatInterval = setInterval(() => {
      if (this.isTracking) {
        this.syncWithServer();
      }
    }, 30000);
  }

  async syncWithServer() {
    // Optional: Send periodic updates to server
    // This could update a "currently working on" status
  }

  saveState() {
    const state = {
      isTracking: this.isTracking,
      currentPlanId: this.currentPlanId,
      startTime: this.startTime,
      elapsedTime: this.elapsedTime,
      timestamp: Date.now()
    };
    localStorage.setItem(this.storageKey, JSON.stringify(state));
  }

  loadState() {
    const saved = localStorage.getItem(this.storageKey);
    if (!saved) return;

    try {
      const state = JSON.parse(saved);
      const timeSinceLastSave = Date.now() - state.timestamp;
      
      // Only restore if less than 5 minutes old
      if (timeSinceLastSave < 5 * 60 * 1000) {
        this.currentPlanId = state.currentPlanId;
        this.startTime = state.startTime;
        this.elapsedTime = state.elapsedTime + timeSinceLastSave;
        
        // Don't auto-resume tracking - let user decide
        this.isTracking = false;
      }
    } catch (error) {
      console.error('Failed to load time tracking state:', error);
    }
  }

  clearState() {
    localStorage.removeItem(this.storageKey);
  }

  formatDuration(ms) {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
      return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    } else {
      return `${seconds}s`;
    }
  }

  // Event listener system
  listeners = {};

  on(event, callback) {
    if (!this.listeners[event]) {
      this.listeners[event] = [];
    }
    this.listeners[event].push(callback);
  }

  off(event, callback) {
    if (this.listeners[event]) {
      this.listeners[event] = this.listeners[event].filter(cb => cb !== callback);
    }
  }

  notifyListeners(event, data) {
    if (this.listeners[event]) {
      this.listeners[event].forEach(callback => callback(data));
    }
  }

  // Public getters
  getIsTracking() {
    return this.isTracking;
  }

  getCurrentPlanId() {
    return this.currentPlanId;
  }

  getElapsedTime() {
    return this.elapsedTime;
  }

  getFormattedElapsed() {
    return this.formatDuration(this.elapsedTime);
  }

  // Cleanup
  destroy() {
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
    
    if (this.worker) {
      this.worker.terminate();
    }
    
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
    }
    
    this.clearState();
  }
}

// Create singleton instance
const timeTrackingService = new TimeTrackingService();

export default timeTrackingService;
